/**
 * SA demo-tenant list page (B-11).
 *
 * Route: /demos
 *
 * Displays all demo tenants (is_demo=true) with aggregate counts.
 * Each row links to the per-demo detail/reset/delete panel at /demos/:slug.
 * "Provision new demo" CTA at top-right navigates to /demos/new.
 *
 * Matches the Tenants.tsx PageHeader + table + StatusPill convention.
 */
import { Link } from "react-router-dom";
import { AlertTriangle } from "lucide-react";
import { PageHeader } from "@/components/admin/PageHeader";
import { CopyButton } from "@/components/common/CopyButton";
import { useAdminDemos } from "@/hooks/queries/admin/useAdminDemos";

/** Path-based per-sandbox login URL (matches the backend `_demo_login_url`). */
function loginUrlFor(slug: string): string {
  return `https://app.certainhr.ai/${slug}/login`;
}

const CTA_GRADIENT =
  "bg-gradient-to-r from-brand-600 to-[var(--p-teal-600)] text-white shadow-sm hover:shadow-md hover:brightness-110 transition";

function DemoPill({ slug }: { slug: string }): JSX.Element {
  return (
    <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs ring-1 ring-amber-300 text-amber-800 dark:bg-amber-500/10 dark:text-amber-200 dark:ring-amber-500/30">
      {slug}
    </span>
  );
}

/**
 * Half-seeded marker. A demo whose seed stopped short is still live and
 * login-able and its raw counts look unremarkable in isolation — this is the
 * only thing on the list page that says "do not demo this one".
 */
function IncompletePill(): JSX.Element {
  return (
    <span
      data-testid="demo-incomplete-pill"
      title="Seeding did not finish — re-run the reset before demoing this tenant."
      className="ml-2 inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-xs font-semibold ring-1 ring-red-300 text-red-800 dark:bg-red-500/10 dark:text-red-200 dark:ring-red-500/30"
    >
      <AlertTriangle size={11} aria-hidden />
      Incomplete
    </span>
  );
}

/** "40 / 100" when short, plain count when whole. */
function CountCell({
  actual,
  expected,
}: {
  actual: number;
  expected: number;
}): JSX.Element {
  const short = expected > 0 && actual < expected;
  return short ? (
    <span className="font-semibold text-red-700 dark:text-red-300">
      {actual} / {expected}
    </span>
  ) : (
    <>{actual}</>
  );
}

export default function DemoList(): JSX.Element {
  const { data, isLoading, error } = useAdminDemos();
  const demos = data?.demos ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-end justify-between">
        <PageHeader title="Demos" />
        <Link
          to="/demos/new"
          className={`rounded-md px-3 py-1.5 text-sm font-medium ${CTA_GRADIENT}`}
        >
          Provision new demo
        </Link>
      </div>

      {error ? (
        <div className="text-sm text-red-600 dark:text-red-400">
          Couldn&apos;t load demo tenants: {error.message}
        </div>
      ) : null}

      <div className="overflow-hidden rounded-xl border border-[var(--p-slate-150)] bg-white shadow-card dark:border-white/[0.06] dark:bg-surface-card dark:shadow-card-dark">
        <table className="w-full text-sm tabular-nums">
          <thead className="bg-surface-canvas text-left text-text-secondary dark:bg-white/[0.03] dark:text-brand-100/60">
            <tr>
              <th className="px-4 py-2 font-medium">Slug</th>
              <th className="px-4 py-2 font-medium">Admin email</th>
              <th className="px-4 py-2 font-medium">Candidates</th>
              <th className="px-4 py-2 font-medium">JDs</th>
              <th className="px-4 py-2 font-medium">Rankings</th>
              <th className="px-4 py-2 font-medium">Last reset</th>
              <th className="px-4 py-2 font-medium">Created</th>
              <th className="px-4 py-2 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && demos.length === 0 ? (
              <tr>
                <td
                  colSpan={8}
                  className="px-4 py-6 text-center text-accent-primary dark:text-brand-100/40"
                >
                  Loading demos…
                </td>
              </tr>
            ) : demos.length === 0 ? (
              <tr>
                <td
                  colSpan={8}
                  className="px-4 py-6 text-center text-accent-primary dark:text-brand-100/40"
                >
                  No demo tenants provisioned yet.{" "}
                  <Link to="/demos/new" className="underline text-accent-primary">
                    Provision one
                  </Link>
                  .
                </td>
              </tr>
            ) : (
              demos.map((d) => (
                <tr
                  key={d.tenant_id}
                  className="border-t border-brand-100 transition-colors hover:bg-brand-50 dark:border-white/[0.04] dark:hover:bg-white/[0.03]"
                >
                  <td className="px-4 py-2">
                    <DemoPill slug={d.slug} />
                    {d.seed_complete ? null : <IncompletePill />}
                  </td>
                  <td className="px-4 py-2 text-text-secondary dark:text-brand-100/80">
                    {d.admin_email ?? "—"}
                  </td>
                  <td className="px-4 py-2 text-text-secondary dark:text-brand-100/80">
                    <CountCell
                      actual={d.candidate_count}
                      expected={d.expected_candidate_count}
                    />
                  </td>
                  <td className="px-4 py-2 text-text-secondary dark:text-brand-100/80">
                    <CountCell actual={d.jd_count} expected={d.expected_jd_count} />
                  </td>
                  <td className="px-4 py-2 text-text-secondary dark:text-brand-100/80">
                    <CountCell
                      actual={d.ranking_count}
                      expected={d.expected_ranking_count}
                    />
                  </td>
                  <td className="px-4 py-2 text-text-secondary dark:text-brand-100/80">
                    {d.last_reset_at
                      ? new Date(d.last_reset_at).toLocaleString()
                      : "Never"}
                  </td>
                  <td className="px-4 py-2 text-text-secondary dark:text-brand-100/80">
                    {new Date(d.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-2">
                    <span className="inline-flex items-center gap-1">
                      <Link
                        to={`/demos/${d.slug}`}
                        className="text-xs font-medium text-accent-primary hover:text-accent-primary-hover dark:hover:text-accent-secondary"
                      >
                        Manage
                      </Link>
                      <CopyButton
                        value={loginUrlFor(d.slug)}
                        ariaLabel={`Copy ${d.slug} login URL`}
                        toastLabel="login URL"
                      />
                    </span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
